import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableCell, TableRow, TableSortLabel,
    IconButton, Tooltip, Switch,
    Popover, Typography, Divider,
    Snackbar, Alert, Box, LinearProgress,
    Chip,
} from '@mui/material';
import { Add, Edit, Delete, AccountBalance } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import { Loader } from '../../../shared/components/Loader';
import { accountService } from '../../../shared/services/accountService';
import { Account } from '../../../shared/types/account';
import { usePermission } from '../../../shared/contexts/PermissionContext';

export const Accounts: React.FC = () => {
    const navigate = useNavigate();
    const { hasPermission, isSuperAdmin } = usePermission();

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortData, setSortData] = useState<{ active: string; direction: 'asc' | 'desc' } | null>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' });
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const fetchAccounts = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await accountService.list({
                page: currentPage + 1,
                limit: pageSize,
                search: searchInput || undefined,
            });

            const outerData = res?.data;
            const items =
                outerData?.data ||
                outerData?.items ||
                (outerData as any)?.accounts ||
                [];

            setAccounts(
                (Array.isArray(items) ? items : []).map((a: any) => ({
                    ...a,
                    _id: a._id ?? a.id,
                    isActive: a.isActive ?? a.is_active ?? true,
                }))
            );
        } catch {
            setSnackbar({ open: true, message: 'Error loading accounts', severity: 'error' });
            setAccounts([]);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, pageSize, searchInput]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    // Client-side filtering & sorting
    const filteredAccounts = React.useMemo(() => {
        let list = [...accounts];
        if (searchInput.trim()) {
            const q = searchInput.trim().toLowerCase();
            list = list.filter(a =>
                a.title?.toLowerCase().includes(q) ||
                a.description?.toLowerCase().includes(q)
            );
        }
        if (sortData?.active) {
            list.sort((a, b) => {
                const av = (a as any)[sortData.active] ?? '';
                const bv = (b as any)[sortData.active] ?? '';
                if (typeof av === 'string' && typeof bv === 'string')
                    return sortData.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                if (av < bv) return sortData.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortData.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return list;
    }, [accounts, searchInput, sortData]);

    const paginatedAccounts = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredAccounts.slice(start, start + pageSize);
    }, [filteredAccounts, currentPage, pageSize]);

    const handleSort = (col: string) => {
        setSortData(prev =>
            prev?.active === col
                ? { active: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { active: col, direction: 'asc' }
        );
    };

    const handleToggleStatus = async (account: Account) => {
        try {
            await accountService.toggleStatus(account._id);
            setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
            fetchAccounts();
        } catch {
            setSnackbar({ open: true, message: 'Error updating status', severity: 'error' });
        }
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(e.currentTarget);
        setSelectedId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedId) return;
        try {
            await accountService.delete(selectedId);
            setSnackbar({ open: true, message: 'Account deleted successfully', severity: 'success' });
            fetchAccounts();
        } catch {
            setSnackbar({ open: true, message: 'Error deleting account', severity: 'error' });
        } finally {
            handleDeleteClose();
        }
    };

    const SortableHeader = ({ col, label }: { col: string; label: string }) => (
        <TableCell className="!font-semibold !text-gray-700">
            <TableSortLabel
                active={sortData?.active === col}
                direction={sortData?.active === col ? sortData.direction : 'asc'}
                onClick={() => handleSort(col)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    return (
        <div className="flex flex-col w-full min-h-full bg-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b border-gray-200 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <AccountBalance className="text-[#6366f1] w-8 h-8" />
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                        Accounts
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={(v) => { setSearchInput(v); setCurrentPage(0); }}
                        onSearch={() => setCurrentPage(0)}
                        placeholder="Search accounts..."
                        className="w-full sm:w-64"
                    />
                    {(isSuperAdmin() || hasPermission('accounts.add')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<Add />}
                            onClick={() => navigate('/accounts/add')}
                            sx={{ px: 3, py: 1.5, borderRadius: '6px' }}
                        >
                            Add Account
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="flex flex-1 flex-col p-6 sm:p-10 overflow-y-auto">
                <div className="flex flex-col w-full flex-1">
                    {isLoading && accounts.length > 0 && <LinearProgress className="mb-4" />}
                    {isLoading && accounts.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : (
                        <AppTable
                            head={
                                <TableRow className="bg-gray-100">
                                    <TableCell className="!font-semibold !text-gray-700">No</TableCell>
                                    <SortableHeader col="title" label="Title" />
                                    <TableCell className="!font-semibold !text-gray-700">Description</TableCell>
                                    <SortableHeader col="isActive" label="Status" />
                                    <TableCell className="!font-semibold !text-gray-700">Actions</TableCell>
                                </TableRow>
                            }
                            isEmpty={filteredAccounts.length === 0}
                            emptyColSpan={5}
                        >
                            {paginatedAccounts.map((item, i) => (
                                <TableRow
                                    key={item._id}
                                    className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                >
                                    <TableCell className="!text-gray-500 !text-sm">
                                        {currentPage * pageSize + i + 1}
                                    </TableCell>
                                    <TableCell className="!text-gray-900 !font-semibold">
                                        {item.title}
                                    </TableCell>
                                    <TableCell className="!text-gray-500 max-w-xs">
                                        {item.description ? (
                                            <span className="line-clamp-2">{item.description}</span>
                                        ) : (
                                            <span className="italic text-gray-300">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={item.isActive}
                                            onChange={() => handleToggleStatus(item)}
                                            disabled={isLoading}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            {(isSuperAdmin() || hasPermission('accounts.update')) && (
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        onClick={() => navigate(`/accounts/edit/${item._id}`)}
                                                        size="small"
                                                        className="text-blue-600 hover:bg-blue-50"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {(isSuperAdmin() || hasPermission('accounts.delete')) && (
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        onClick={(e) => handleDeleteClick(e, item._id)}
                                                        size="small"
                                                        className="text-red-500 hover:bg-red-50"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </AppTable>
                    )}
                </div>

                <AppTablePagination
                    totalRows={filteredAccounts.length}
                    page={currentPage}
                    pageSize={pageSize}
                    onPageChange={(_, p) => setCurrentPage(p - 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
                />
            </div>

            {/* Delete Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        p: 0, mt: 1.5, width: 260,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Delete Account?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This account will be soft-deleted and hidden from the list.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton variant="secondary" size="small" onClick={handleDeleteClose}>Cancel</AppButton>
                        <AppButton variant="danger" size="small" onClick={handleDeleteConfirm}>Delete</AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity as any} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
